import { useState } from "react";
import { Icon } from "../components/Icon.tsx";
import {
  Button,
  Dialog,
  EmptyState,
  Field,
  Skeleton,
  StatusBadge,
  type Tone,
} from "../components/ui.tsx";
import { locale, t, tn, type MessageKey } from "../i18n/index.ts";
import { action } from "../lib/api.ts";
import { useLiveAction } from "../lib/data.ts";
import { todayIso } from "../lib/format.ts";
import { refreshTeam } from "../lib/team.ts";
import { useUi } from "../lib/ui-state.tsx";
import { useSave } from "./manage/common.tsx";

/**
 * Planning on the team screen: positions (who can do which job), open shifts anyone qualified
 * can take, and swaps between colleagues. The rota shows them to the manager; "Me" shows the
 * shifts a person can pick up and lets them offer their own.
 */

export const TONES = [
  "blue",
  "green",
  "yellow",
  "orange",
  "red",
  "purple",
  "teal",
  "grey",
] as const;
export type PositionTone = (typeof TONES)[number];

export interface Position {
  id: string;
  name: string;
  tone: PositionTone;
  active: boolean;
  sequence: number;
  users: string[];
}

export interface OpenShiftRow {
  id: string;
  location: string;
  location_name: string;
  position: string | null;
  day: string;
  start: string;
  end: string;
  label: string | null;
  note: string | null;
  state: "draft" | "published" | "taken" | "cancelled";
  taken_name: string | null;
  minutes: number;
}

export interface PlanningIssues {
  week_limit: number;
  over_limit: { user: string; name: string; minutes: number }[];
  open_unfilled: number;
  swaps_waiting: number;
}

interface SwapShift {
  id: string;
  day: string;
  start: string;
  end: string;
  label: string | null;
  location_name: string;
  minutes: number;
  from: string;
}
interface SwapRow {
  id: string;
  state: "offered" | "claimed" | "approved" | "declined" | "withdrawn";
  reason: string | null;
  note: string | null;
  from_user: string;
  from_name: string;
  to_user: string | null;
  to_name: string | null;
  shift: SwapShift;
}

const weekday = (d: string) =>
  new Intl.DateTimeFormat(locale(), {
    weekday: "short",
    timeZone: "UTC",
  }).format(new Date(`${d}T00:00:00Z`));
const shortDay = (d: string) =>
  new Intl.DateTimeFormat(locale(), {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  }).format(new Date(`${d}T00:00:00Z`));
const when = (s: { day: string; start: string; end: string }) =>
  `${weekday(s.day)} ${shortDay(s.day)} · ${s.start}–${s.end}`;
const hm = (minutes: number) =>
  minutes % 60
    ? t("time.hours", { h: Math.floor(minutes / 60), m: minutes % 60 })
    : t("team.h", { h: minutes / 60 });

/** A small coloured dot and name for a position. */
export function PositionTag({
  p,
}: {
  p: Pick<Position, "name" | "tone"> | null | undefined;
}) {
  if (!p) return null;
  return (
    <span className="pos-tag">
      <span className={`pos-dot tone-${p.tone}`} aria-hidden="true" />
      {p.name}
    </span>
  );
}

// ---- rota: warnings ------------------------------------------------------------------------

export function PlanningBar({
  issues,
  onSwaps,
}: {
  issues: PlanningIssues;
  onSwaps: () => void;
}) {
  if (
    !issues.over_limit.length &&
    !issues.swaps_waiting &&
    !issues.open_unfilled
  )
    return null;
  return (
    <div className="plan-bar" role="status">
      {issues.swaps_waiting ? (
        <button className="plan-pill warn" onClick={onSwaps}>
          <Icon name="repeat" size={16} />{" "}
          {tn("plan.swaps_waiting", issues.swaps_waiting)}
          <Icon name="chevronRight" size={14} />
        </button>
      ) : null}
      {issues.open_unfilled ? (
        <span className="plan-pill">
          <Icon name="inbox" size={16} />{" "}
          {tn("plan.open_unfilled", issues.open_unfilled)}
        </span>
      ) : null}
      {issues.over_limit.map((o) => (
        <span key={o.user} className="plan-pill danger">
          <Icon name="alert" size={16} />{" "}
          {t("plan.over_limit", {
            name: o.name,
            h: hm(o.minutes),
            limit: hm(issues.week_limit),
          })}
        </span>
      ))}
    </div>
  );
}

// ---- rota: positions ------------------------------------------------------------------------

export function PositionsDialog({
  people,
  onClose,
  onSaved,
}: {
  people: { id: string; name: string }[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { data, reload } = useLiveAction<{ positions: Position[] }>(
    "team.positions",
    {},
    ["team"],
  );
  const [editing, setEditing] = useState<Partial<Position> | null>(null);
  return (
    <Dialog title={t("plan.positions")} onClose={onClose} size="lg">
      <p className="muted">{t("plan.positions_hint")}</p>
      {!data ? (
        <Skeleton h={160} />
      ) : (
        <ul className="pos-list">
          {data.positions.map((p) => (
            <li key={p.id} className={p.active ? undefined : "is-off"}>
              <PositionTag p={p} />
              <span className="muted small pos-who">
                {p.users.length
                  ? p.users
                      .map((u) => people.find((x) => x.id === u)?.name ?? "")
                      .filter(Boolean)
                      .join(", ")
                  : t("plan.anyone")}
              </span>
              {!p.active ? (
                <StatusBadge
                  tone="neutral"
                  icon="archive"
                  label={t("plan.off")}
                />
              ) : null}
              <Button
                variant="ghost"
                size="sm"
                icon="edit"
                onClick={() => setEditing(p)}
              >
                {t("common.edit")}
              </Button>
            </li>
          ))}
        </ul>
      )}
      <Button
        icon="plus"
        onClick={() =>
          setEditing({
            tone: TONES[(data?.positions.length ?? 0) % TONES.length],
            users: [],
            active: true,
          })
        }
      >
        {t("plan.add_position")}
      </Button>
      {editing ? (
        <PositionDialog
          position={editing}
          people={people}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await reload();
            onSaved();
          }}
        />
      ) : null}
    </Dialog>
  );
}

function PositionDialog({
  position,
  people,
  onClose,
  onSaved,
}: {
  position: Partial<Position>;
  people: { id: string; name: string }[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { run, busy } = useSave();
  const [name, setName] = useState(position.name ?? "");
  const [tone, setTone] = useState<PositionTone>(position.tone ?? "blue");
  const [users, setUsers] = useState<string[]>(position.users ?? []);
  const [active, setActive] = useState(position.active ?? true);
  const toggle = (id: string) =>
    setUsers((u) => (u.includes(id) ? u.filter((x) => x !== id) : [...u, id]));
  const save = () =>
    void run(async () => {
      await action("team.save_position", {
        id: position.id ?? null,
        name: name.trim(),
        tone,
        active,
        users,
      });
      onSaved();
    }, t("plan.position_saved"));
  const remove = () =>
    void run(async () => {
      const r = await action<{ deleted: boolean }>("team.delete_position", {
        id: position.id,
      });
      if (!r.deleted) setActive(false);
      onSaved();
    }, t("plan.position_removed"));
  return (
    <Dialog
      title={position.id ? t("plan.edit_position") : t("plan.add_position")}
      onClose={onClose}
      footer={
        <>
          {position.id ? (
            <Button variant="ghost" icon="trash" onClick={remove}>
              {t("common.delete")}
            </Button>
          ) : null}
          <span className="spacer" />
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            disabled={!name.trim()}
            onClick={save}
          >
            {t("common.save")}
          </Button>
        </>
      }
    >
      <Field
        label={t("plan.position_name")}
        hint={t("plan.position_name_hint")}
      >
        <input
          className="input"
          value={name}
          maxLength={40}
          onChange={(e) => setName(e.target.value)}
          data-autofocus
        />
      </Field>
      <fieldset className="pos-tones">
        <legend>{t("plan.colour")}</legend>
        {TONES.map((x) => (
          <button
            key={x}
            className={`pos-swatch tone-${x}${tone === x ? " active" : ""}`}
            aria-pressed={tone === x}
            aria-label={t(`plan.tone_${x}` as MessageKey)}
            onClick={() => setTone(x)}
          >
            {tone === x ? <Icon name="check" size={16} /> : null}
          </button>
        ))}
      </fieldset>
      <fieldset className="pos-people">
        <legend>{t("plan.who_can")}</legend>
        <p className="muted small">{t("plan.who_can_hint")}</p>
        <div className="chips">
          {people.map((p) => (
            <button
              key={p.id}
              className={`chip${users.includes(p.id) ? " active" : ""}`}
              aria-pressed={users.includes(p.id)}
              onClick={() => toggle(p.id)}
            >
              {users.includes(p.id) ? <Icon name="check" size={14} /> : null}{" "}
              {p.name}
            </button>
          ))}
        </div>
      </fieldset>
      {position.id ? (
        <label className="check-row">
          <input
            type="checkbox"
            checked={active}
            onChange={(e) => setActive(e.target.checked)}
          />{" "}
          {t("plan.in_use")}
        </label>
      ) : null}
    </Dialog>
  );
}

// ---- rota: open shifts ----------------------------------------------------------------------

export function OpenShiftDialog({
  open,
  positions,
  people,
  onClose,
  onSaved,
}: {
  open: Partial<OpenShiftRow>;
  positions: Position[];
  people: { id: string; name: string }[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { run, busy } = useSave();
  const [position, setPosition] = useState(open.position ?? "");
  const [d, setD] = useState(open.day ?? todayIso());
  const [start, setStart] = useState(open.start ?? "18:00");
  const [end, setEnd] = useState(open.end ?? "23:00");
  const [note, setNote] = useState(open.note ?? "");
  const [count, setCount] = useState(1);
  const [assignee, setAssignee] = useState("");
  const pos = positions.find((p) => p.id === position);
  const eligible = people.filter(
    (p) => !pos || !pos.users.length || pos.users.includes(p.id),
  );
  const locked = open.state === "taken" || open.state === "cancelled";
  const save = () =>
    void run(
      async () => {
        await action("team.save_open_shift", {
          id: open.id ?? null,
          location: open.location,
          position: position || null,
          day: d,
          start,
          end,
          label: pos?.name ?? null,
          note: note || null,
          count,
        });
        onSaved();
      },
      open.id ? t("plan.open_saved") : tn("plan.open_added", count),
    );
  const cancel = () =>
    void run(async () => {
      await action("team.cancel_open_shift", { id: open.id });
      onSaved();
    }, t("plan.open_cancelled"));
  const assign = () =>
    void run(
      async () => {
        await action("team.assign_open_shift", { id: open.id, user: assignee });
        refreshTeam();
        onSaved();
      },
      t("plan.assigned", {
        name: people.find((p) => p.id === assignee)?.name ?? "",
      }),
    );
  return (
    <Dialog
      title={open.id ? t("plan.edit_open") : t("plan.add_open")}
      onClose={onClose}
      footer={
        locked ? (
          <Button variant="ghost" onClick={onClose}>
            {t("common.close")}
          </Button>
        ) : (
          <>
            {open.id ? (
              <Button variant="ghost" icon="trash" onClick={cancel}>
                {t("plan.cancel_open")}
              </Button>
            ) : null}
            <span className="spacer" />
            <Button variant="ghost" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button variant="primary" icon="check" busy={busy} onClick={save}>
              {t("common.save")}
            </Button>
          </>
        )
      }
    >
      {open.state === "taken" ? (
        <p className="callout callout-info">
          <Icon name="checkCircle" size={16} />{" "}
          {t("plan.taken_by", { name: open.taken_name ?? "" })}
        </p>
      ) : (
        <p className="muted">
          {open.state === "published"
            ? t("plan.open_published_hint")
            : t("plan.open_hint")}
        </p>
      )}
      <div className="form-grid">
        <Field label={t("plan.position")}>
          <select
            className="input"
            value={position}
            onChange={(e) => setPosition(e.target.value)}
            disabled={locked}
          >
            <option value="">{t("plan.any_position")}</option>
            {positions
              .filter((p) => p.active || p.id === position)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
          </select>
        </Field>
        <Field label={t("team.day")}>
          <input
            className="input"
            type="date"
            value={d}
            onChange={(e) => setD(e.target.value)}
            disabled={locked}
          />
        </Field>
        <Field label={t("team.from")}>
          <input
            className="input"
            type="time"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            disabled={locked}
          />
        </Field>
        <Field
          label={t("team.to")}
          hint={end <= start && end !== start ? t("team.overnight") : undefined}
        >
          <input
            className="input"
            type="time"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            disabled={locked}
          />
        </Field>
        {!open.id ? (
          <Field label={t("plan.how_many")} hint={t("plan.how_many_hint")}>
            <input
              className="input"
              type="number"
              min={1}
              max={20}
              value={count}
              onChange={(e) =>
                setCount(Math.max(1, Math.min(20, Number(e.target.value) || 1)))
              }
            />
          </Field>
        ) : null}
      </div>
      <Field label={t("team.note")}>
        <input
          className="input"
          value={note}
          maxLength={200}
          placeholder={t("plan.note_ph")}
          onChange={(e) => setNote(e.target.value)}
          disabled={locked}
        />
      </Field>
      {open.id && !locked ? (
        <div className="plan-assign">
          <Field label={t("plan.assign")} hint={t("plan.assign_hint")}>
            <select
              className="input"
              value={assignee}
              onChange={(e) => setAssignee(e.target.value)}
            >
              <option value="">{t("plan.choose_person")}</option>
              {eligible.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>
          <Button
            icon="users"
            disabled={!assignee}
            busy={busy}
            onClick={assign}
          >
            {t("plan.assign_btn")}
          </Button>
        </div>
      ) : null}
    </Dialog>
  );
}

// ---- rota: swaps waiting --------------------------------------------------------------------

const SWAP_LOOK: Record<
  SwapRow["state"],
  { tone: Tone; icon: "repeat" | "clock" | "checkCircle" | "x" | "undo" }
> = {
  offered: { tone: "info", icon: "repeat" },
  claimed: { tone: "warn", icon: "clock" },
  approved: { tone: "ok", icon: "checkCircle" },
  declined: { tone: "danger", icon: "x" },
  withdrawn: { tone: "neutral", icon: "undo" },
};

export function SwapBadge({ state }: { state: SwapRow["state"] }) {
  return (
    <StatusBadge
      tone={SWAP_LOOK[state].tone}
      icon={SWAP_LOOK[state].icon}
      label={t(`plan.swap_${state}` as MessageKey)}
    />
  );
}

export function SwapsDialog({
  location,
  onClose,
  onDone,
}: {
  location: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const { data, reload } = useLiveAction<{
    waiting: SwapRow[];
    offered: SwapRow[];
    done: SwapRow[];
  }>("team.swaps", { location }, ["team"]);
  const { fail, toast } = useUi();
  const [busy, setBusy] = useState<string | null>(null);
  const [declining, setDeclining] = useState<SwapRow | null>(null);
  const decide = async (w: SwapRow, approve: boolean, note?: string) => {
    setBusy(w.id);
    try {
      await action("team.decide_swap", {
        id: w.id,
        approve,
        note: note ?? null,
      });
      toast(
        approve
          ? t("plan.approved_toast", { name: w.to_name ?? "" })
          : t("plan.declined_toast"),
      );
      setDeclining(null);
      await reload();
      refreshTeam();
      onDone();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(null);
    }
  };
  return (
    <Dialog title={t("plan.swaps")} onClose={onClose} size="lg">
      {!data ? (
        <Skeleton h={160} />
      ) : !data.waiting.length && !data.offered.length && !data.done.length ? (
        <EmptyState
          icon="repeat"
          title={t("plan.no_swaps")}
          body={t("plan.no_swaps_hint")}
        />
      ) : (
        <div className="swap-groups">
          {data.waiting.length ? (
            <section>
              <h3>{t("plan.swaps_need_you")}</h3>
              <ul className="swap-list">
                {data.waiting.map((w) => (
                  <li key={w.id}>
                    <SwapText w={w} />
                    <div className="swap-actions">
                      <Button
                        variant="ghost"
                        icon="x"
                        disabled={!!busy}
                        onClick={() => setDeclining(w)}
                      >
                        {t("plan.decline")}
                      </Button>
                      <Button
                        variant="primary"
                        icon="check"
                        busy={busy === w.id}
                        onClick={() => void decide(w, true)}
                      >
                        {t("plan.approve")}
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          {data.offered.length ? (
            <section>
              <h3>{t("plan.swaps_offered")}</h3>
              <ul className="swap-list">
                {data.offered.map((w) => (
                  <li key={w.id}>
                    <SwapText w={w} />
                    <SwapBadge state={w.state} />
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          {data.done.length ? (
            <section>
              <h3>{t("plan.swaps_done")}</h3>
              <ul className="swap-list">
                {data.done.map((w) => (
                  <li key={w.id}>
                    <SwapText w={w} />
                    <SwapBadge state={w.state} />
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      )}
      {declining ? (
        <DeclineDialog
          busy={busy === declining.id}
          onClose={() => setDeclining(null)}
          onDecline={(note) => void decide(declining, false, note)}
        />
      ) : null}
    </Dialog>
  );
}

function SwapText({ w }: { w: SwapRow }) {
  return (
    <span className="swap-text">
      <strong>{when(w.shift)}</strong>
      <span>
        {w.to_name
          ? t("plan.swap_from_to", { from: w.from_name, to: w.to_name })
          : t("plan.swap_from", { from: w.from_name })}
        {w.shift.label ? ` · ${w.shift.label}` : ""}
      </span>
      {w.reason ? <span className="muted small">“{w.reason}”</span> : null}
      {w.note ? (
        <span className="muted small">
          {t("plan.manager_note", { note: w.note })}
        </span>
      ) : null}
    </span>
  );
}

function DeclineDialog({
  busy,
  onClose,
  onDecline,
}: {
  busy: boolean;
  onClose: () => void;
  onDecline: (note: string) => void;
}) {
  const [note, setNote] = useState("");
  return (
    <Dialog
      title={t("plan.decline_title")}
      size="sm"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="danger"
            icon="x"
            busy={busy}
            disabled={note.trim().length < 3}
            onClick={() => onDecline(note.trim())}
          >
            {t("plan.decline")}
          </Button>
        </>
      }
    >
      <Field label={t("plan.decline_why")} hint={t("plan.decline_hint")}>
        <input
          className="input"
          value={note}
          maxLength={200}
          onChange={(e) => setNote(e.target.value)}
          data-autofocus
        />
      </Field>
    </Dialog>
  );
}

// ---- me: the shift market --------------------------------------------------------------------

interface MarketOpen {
  id: string;
  day: string;
  start: string;
  end: string;
  note: string | null;
  minutes: number;
  location_name: string;
  position: { id: string; name: string; tone: PositionTone } | null;
  busy: string | null;
}
type MarketOffer = Omit<SwapRow, "shift"> & {
  shift: SwapShift & { position: MarketOpen["position"] };
  busy: string | null;
};
interface Market {
  open: MarketOpen[];
  offers: MarketOffer[];
  swaps: (Omit<SwapRow, "shift"> & {
    shift: SwapShift & { position: MarketOpen["position"] };
    mine: boolean;
  })[];
  approval: boolean;
}

/** Live swaps of the person's own shifts, so "My shifts" can show which ones are offered. */
export function useMarket() {
  return useLiveAction<Market>("team.market", {}, ["team"]);
}

export function liveSwapOf(market: Market | null, shift: string) {
  return market?.swaps.find(
    (w) =>
      w.mine &&
      w.shift.id === shift &&
      (w.state === "offered" || w.state === "claimed"),
  );
}

export function MarketCard({
  market,
  multi,
  reload,
}: {
  market: Market | null;
  multi: boolean;
  reload: () => Promise<unknown>;
}) {
  const { fail, toast } = useUi();
  const [busy, setBusy] = useState<string | null>(null);
  const go = async (key: string, fn: () => Promise<unknown>, done: string) => {
    setBusy(key);
    try {
      await fn();
      toast(done);
      await reload();
      refreshTeam();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(null);
    }
  };
  if (!market) return null;
  const others = market.swaps.filter((w) => !w.mine || w.state !== "offered");
  const hasAny = market.open.length || market.offers.length;
  return (
    <>
      <section className="card">
        <header className="card-head">
          <div>
            <h2>{t("plan.pick_up")}</h2>
            <span className="muted">{t("plan.pick_up_hint")}</span>
          </div>
        </header>
        {!hasAny ? (
          <p className="muted">{t("plan.nothing_open")}</p>
        ) : (
          <ul className="market-list">
            {market.open.map((o) => (
              <li key={o.id}>
                <span className="market-kind open">
                  <Icon name="inbox" size={18} />
                </span>
                <span className="market-text">
                  <strong>{when(o)}</strong>
                  <span className="muted small">
                    {[
                      t("plan.open_shift"),
                      multi ? o.location_name : null,
                      hm(o.minutes),
                      o.note,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                  <PositionTag p={o.position} />
                </span>
                {o.busy ? (
                  <span className="muted small market-busy">
                    {t("plan.you_work", { time: o.busy })}
                  </span>
                ) : (
                  <Button
                    variant="primary"
                    icon="check"
                    busy={busy === o.id}
                    disabled={!!busy}
                    onClick={() =>
                      void go(
                        o.id,
                        () => action("team.take_open_shift", { id: o.id }),
                        t("plan.taken_toast"),
                      )
                    }
                  >
                    {t("plan.take")}
                  </Button>
                )}
              </li>
            ))}
            {market.offers.map((w) => (
              <li key={w.id}>
                <span className="market-kind swap">
                  <Icon name="repeat" size={18} />
                </span>
                <span className="market-text">
                  <strong>{when(w.shift)}</strong>
                  <span className="muted small">
                    {[
                      t("plan.offered_by", { name: w.from_name }),
                      multi ? w.shift.location_name : null,
                      hm(w.shift.minutes),
                      w.reason ? `“${w.reason}”` : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                  <PositionTag p={w.shift.position} />
                </span>
                {w.busy ? (
                  <span className="muted small market-busy">
                    {t("plan.you_work", { time: w.busy })}
                  </span>
                ) : (
                  <Button
                    variant="primary"
                    icon="repeat"
                    busy={busy === w.id}
                    disabled={!!busy}
                    onClick={() =>
                      void go(
                        w.id,
                        () => action("team.claim_offer", { id: w.id }),
                        market.approval
                          ? t("plan.claimed_toast")
                          : t("plan.claimed_now_toast"),
                      )
                    }
                  >
                    {t("plan.claim")}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
      {others.length ? (
        <section className="card">
          <header className="card-head">
            <h2>{t("plan.my_swaps")}</h2>
          </header>
          <ul className="swap-list">
            {others.map((w) => (
              <li key={w.id}>
                <span className="swap-text">
                  <strong>{when(w.shift)}</strong>
                  <span>
                    {w.mine
                      ? w.to_name
                        ? t("plan.you_to", { name: w.to_name })
                        : t("plan.you_offered")
                      : t("plan.from_you", { name: w.from_name })}
                  </span>
                  {w.note ? (
                    <span className="muted small">
                      {t("plan.manager_note", { note: w.note })}
                    </span>
                  ) : null}
                </span>
                <span className="swap-actions">
                  <SwapBadge state={w.state} />
                  {w.state === "claimed" ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      icon="undo"
                      busy={busy === w.id}
                      onClick={() =>
                        void go(
                          w.id,
                          () => action("team.withdraw_offer", { id: w.id }),
                          t("plan.withdrawn_toast"),
                        )
                      }
                    >
                      {w.mine ? t("plan.withdraw") : t("plan.step_back")}
                    </Button>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </>
  );
}

export function OfferDialog({
  shift,
  onClose,
  onDone,
}: {
  shift: { id: string; day: string; start: string; end: string };
  onClose: () => void;
  onDone: () => void;
}) {
  const { run, busy } = useSave();
  const [reason, setReason] = useState("");
  return (
    <Dialog
      title={t("plan.offer_title")}
      size="sm"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="primary"
            icon="repeat"
            busy={busy}
            onClick={() =>
              void run(async () => {
                await action("team.offer_shift", {
                  shift: shift.id,
                  reason: reason.trim() || null,
                });
                onDone();
              }, t("plan.offered_toast"))
            }
          >
            {t("plan.offer")}
          </Button>
        </>
      }
    >
      <p>
        <strong>{when(shift)}</strong>
      </p>
      <p className="muted">{t("plan.offer_hint")}</p>
      <Field label={t("plan.offer_why")}>
        <input
          className="input"
          value={reason}
          maxLength={200}
          placeholder={t("plan.offer_why_ph")}
          onChange={(e) => setReason(e.target.value)}
          data-autofocus
        />
      </Field>
    </Dialog>
  );
}
